import React, { useEffect, useState } from 'react';
import { Input, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import api from '../../helpers/api';

/**
 * Multi-user picker for users who hold a given permission.
 * Stores user IDs in `value`. Same UX pattern as AssignTechnicianModal:
 * dropdown to add + badge row of selected with × to remove.
 */
const RecipientUserMultiPicker = ({
  value = [],
  onChange,
  organizationId,
  permission = 'materiovigilance:read',
  disabled,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('user/with-permission', {
          params: { organizationId, permission },
        });
        if (!cancelled) setUsers(data?.response || []);
      } catch (e) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId, permission]);

  const addUser = (id) => {
    const num = parseInt(id, 10);
    if (!num || value.includes(num)) return;
    onChange([...value, num]);
  };
  const removeUser = (id) => onChange(value.filter((v) => v !== id));

  if (loading) {
    return <div className="text-muted small"><Spinner size="sm" className="me-2" />{t('shared.loading', 'Loading…')}</div>;
  }

  return (
    <>
      <Input
        type="select"
        value=""
        disabled={disabled}
        onChange={(e) => { addUser(e.target.value); e.target.value = ''; }}
      >
        <option value="">
          {t('compliance.materiovigilance.descending.field.recipients_placeholder', 'Select a user to add')}
        </option>
        {users
          .filter((u) => !value.includes(u.id))
          .map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
      </Input>

      <div className="d-flex flex-wrap gap-2 mt-2">
        {value.map((id) => {
          const u = users.find((x) => x.id === id);
          if (!u) return null;
          return (
            <span
              key={id}
              className="badge bg-primary d-flex align-items-center gap-1"
              style={{ fontSize: 12, padding: '6px 10px' }}
            >
              <FeatherIcon icon="user" size={12} />
              {u.name}
              {!disabled && (
                <button
                  type="button"
                  className="btn-close btn-close-white ms-1"
                  style={{ fontSize: 8 }}
                  onClick={() => removeUser(id)}
                  aria-label={t('shared.remove', 'Remove')}
                />
              )}
            </span>
          );
        })}
      </div>

      {value.length === 0 && (
        <small className="text-muted">
          {t('compliance.materiovigilance.descending.field.recipients_empty', 'No recipients selected')}
        </small>
      )}
    </>
  );
};

export default RecipientUserMultiPicker;
